import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { feedbackService, ResponseWithAnswersDto, FeedbackStructure } from '../services/feedbackService';
import { useFeedback } from '../context/FeedbackContext';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  FileText,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

const ResponseViewer: React.FC = () => {
  const { feedbackId, instanceId } = useParams<{ feedbackId: string; instanceId: string }>();
  const navigate = useNavigate();
  const { feedbackList } = useFeedback();

  const [response, setResponse] = useState<ResponseWithAnswersDto | null>(null);
  const [feedbackStructure, setFeedbackStructure] = useState<FeedbackStructure | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const feedback = feedbackList.find(s => s.id === feedbackId);

  useEffect(() => {
    const loadData = async () => {
      if (!instanceId || !feedbackId) return;

      try {
        setLoading(true);
        setError(null);

        const responseData = await feedbackService.getMyResponseForInstance(instanceId);

        if (!responseData) {
          setError('No response found for this feedback instance.');
          return;
        }

        setResponse(responseData);

        const structureData = await feedbackService.getFeedbackStructure(feedbackId, responseData.versionNo);
        setFeedbackStructure(structureData);
      } catch (err: any) {
        setError(err.message || 'Failed to load response data.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [instanceId, feedbackId]);

  const resolveChoiceText = (questionName: string, optionValue: string): string => {
    if (!feedbackStructure?.pages) return optionValue;
    for (const page of feedbackStructure.pages) {
      const question = page.questions?.find(q => q.name === questionName);
      if (question?.choices) {
        const choice = question.choices.find(c => c.value === optionValue);
        if (choice) return choice.text;
      }
    }
    return optionValue;
  };

  const formatAnswerValue = (value: any, questionType: string, questionName: string): string => {
    if (value === null || value === undefined) return 'No answer provided';

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (Array.isArray(value)) {
      return value.map(v => resolveChoiceText(questionName, String(v))).join(', ');
    }

    if (questionType === 'rating') {
      const question = feedbackStructure?.pages
        ?.flatMap(p => p.questions || [])
        ?.find(q => q.name === questionName);
      const max = question?.config?.rateMax || 5;
      return `${value} / ${max}`;
    }

    if (typeof value === 'object') {
      const entries = Object.entries(value);
      if (entries.length === 0) return 'No answer provided';
      return entries.map(([key, val]) => `${key}: ${val}`).join(', ');
    }

    if (questionType === 'multipleChoice' || questionType === 'radiogroup' || questionType === 'dropdown' || questionType === 'checkbox') {
      return resolveChoiceText(questionName, String(value));
    }

    return String(value);
  };

  const getQuestionTitle = (questionName: string): string => {
    if (!feedbackStructure?.pages) return questionName;

    for (const page of feedbackStructure.pages) {
      const question = page.questions?.find(q => q.name === questionName);
      if (question) return question.title || questionName;
    }

    return questionName;
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-6" />
          <div className="bg-gray-200 dark:bg-gray-700 rounded-lg h-48 mb-6" />
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-6">
        <button
          onClick={() => navigate('/feedback/assigned')}
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to My Feedback
        </button>

        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            <div>
              <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">
                Unable to Load Response
              </h3>
              <p className="text-red-600 dark:text-red-400 mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!response) return null;

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/feedback/assigned')}
        className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to My Feedback
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {feedback?.title || 'Feedback Response'}
            </h1>
            {feedback?.description && (
              <p className="text-gray-600 dark:text-gray-400 mt-1">{feedback.description}</p>
            )}
          </div>
          <span className="inline-flex items-center px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded-full text-sm font-medium">
            <CheckCircle className="h-4 w-4 mr-1" />
            Submitted
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          {response.respondent && (
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-gray-400" />
              <span className="text-gray-600 dark:text-gray-400">Submitted by:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {response.respondent.fullName}
              </span>
            </div>
          )}
          {response.submittedAt && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-gray-400" />
              <span className="text-gray-600 dark:text-gray-400">Submitted:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {new Date(response.submittedAt).toLocaleDateString()} at{' '}
                {new Date(response.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4 text-gray-400" />
            <span className="text-gray-600 dark:text-gray-400">Version:</span>
            <span className="font-medium text-gray-900 dark:text-white">
              v{response.versionNo}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Your Responses
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {response.answers.length} question{response.answers.length !== 1 ? 's' : ''} answered
          </p>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {response.answers.map((answer, index) => (
            <div key={answer.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                    {getQuestionTitle(answer.questionName)}
                  </h3>
                  <div className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 rounded-md p-3 whitespace-pre-wrap">
                    {answer.displayValue || formatAnswerValue(answer.value, answer.questionType, answer.questionName)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {response.answers.length === 0 && (
          <div className="p-8 text-center">
            <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">No answers recorded for this response.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResponseViewer;
